import { EventDispatcher } from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'

import { getLookAtPoint, uvDistance, uvOpposite, worldToUv } from '@/lib/site-viewer/utils'
import type { Duration } from '@/lib/site-viewer/tween-camera'
import { tweenCamera } from '@/lib/site-viewer/tween-camera'
import { DEFAULT_ZOOM_TWEEN } from '@/lib/site-viewer/constants'

import type { Vector3, BaseEvent } from 'three'
import type { SiteViewer } from '@/lib/site-viewer/site-viewer'
import type { Origin } from '@/entities/site'
import type { SiteCamera } from '@/entities/siteCamera'
import type { Vec2Data } from '@/entities/model3d'

export enum ControlEvent {
  ViewChangedEvent = 'view-changed',
  StartTween = 'start-tween',
  StopTween = 'stop-tween',
  Pan = 'pan',
  Zoom = 'zoom',
  InitializedCamera = 'initialized',
  PointerDown = 'pointer-down',
  Wheel = 'wheel',
}

export interface ControlEventWrapper extends BaseEvent {
  [ControlEvent.ViewChangedEvent]: { payload?: any }
  [ControlEvent.StartTween]: { payload?: any }
  [ControlEvent.StopTween]: { payload?: any }
  [ControlEvent.Pan]: { payload?: any }
  [ControlEvent.Zoom]: { payload?: any }
  [ControlEvent.InitializedCamera]: { payload?: any }
  [ControlEvent.PointerDown]: { payload?: any }
  [ControlEvent.Wheel]: { payload?: any }
}

export abstract class SiteViewerControls extends EventDispatcher<ControlEventWrapper> {
  orbitControls: OrbitControls | null = null
  viewer: SiteViewer | null = null
  private _lastPosition: Vector3 | null = null
  private _killFovTween: () => void = () => {}
  private _isTweening: boolean = false

  attach(viewer: SiteViewer): void {
    this.viewer = viewer

    const controls = new OrbitControls(viewer.camera, viewer.cssRenderer.domElement)
    controls.rotateSpeed *= -0.5
    controls.enablePan = false
    controls.enableZoom = false
    controls.enableDamping = true
    controls.dampingFactor = 0.1
    controls.maxPolarAngle = (Math.PI * 23) / 24
    controls.minPolarAngle = Math.PI / 24

    this._lastPosition = viewer.camera.position.clone()
    controls.addEventListener('change', this._publishPan.bind(this))

    this.orbitControls = controls
  }
  abstract setInitialView({ position, tweenDuration }: { position?: Vec2Data; tweenDuration?: number }): Promise<void>
  abstract lookAt(props: { position: Vec2Data; tweenDuration?: Duration; onComplete?: () => void }): void
  abstract resetZoom(): void
  abstract startAutoRotate(): void
  abstract update(): void
  detach(): void {
    this._killFovTween()
  }

  get isTweening() {
    return this._isTweening
  }

  set isTweening(newVal: boolean) {
    if (newVal !== this._isTweening) {
      this._isTweening = newVal
      this.dispatchEvent({ type: newVal ? ControlEvent.StartTween : ControlEvent.StopTween })
    }
  }

  async setCameraData(
    cameraData: SiteCamera | null,
    { currentState, tweenDuration }: { currentState: Partial<{ coordinate: Origin | null; zoom: number | null }>; tweenDuration?: number },
  ): Promise<void> {
    let position: Vec2Data | undefined
    if (currentState.coordinate) {
      position = { x: currentState.coordinate.x, y: 1 - currentState.coordinate.y }
    } else if (cameraData) {
      position = worldToUv(getLookAtPoint({ x: cameraData.pan_x as number, y: cameraData.pan_y as number }))
    }

    if (currentState.zoom) {
      await this.updateZoom({ fov: currentState.zoom, shouldPublish: true })
    } else if (cameraData?.zoom) {
      await this.updateZoom({ fov: cameraData.zoom, shouldPublish: true })
    }

    await this.setInitialView({ position, tweenDuration })
    this.dispatchEvent({ type: ControlEvent.InitializedCamera })
  }

  get targetPosition() {
    if (!this.orbitControls || !this.viewer) return null
    const controls = this.orbitControls
    // panorama socket channel expects UV coordinates so we take our world camera position and convert it back into UV coordinates
    const cameraToTarget = controls.target.clone().sub(this.viewer.camera.position).normalize()
    const point = worldToUv(cameraToTarget)
    return point
  }

  protected _publishPan() {
    if (!this.viewer) return

    const point = this.targetPosition
    if (!point) return

    // skip if there is no change since last pan (for example, when the FOV or aspect changed)
    if (this._lastPosition?.equals(this.viewer.camera.position)) {
      return
    }

    this.dispatchEvent({
      type: ControlEvent.Pan,
      payload: { coordinate: { x: point.x, y: 1 - point.y }, duration: 0.5 },
    })
    this._lastPosition = this.viewer.camera.position.clone()
  }

  updateZoom({
    fov,
    shouldPublish = false,
    tweenDuration = DEFAULT_ZOOM_TWEEN,
  }: {
    fov: number
    shouldPublish?: boolean
    tweenDuration?: number
  }): Promise<void> {
    return new Promise(resolve => {
      if (!this.viewer) return

      const clamped = this.viewer.clampFov(fov)

      this._killFovTween()
      this._killFovTween = tweenCamera(
        this.viewer.camera,
        { fov: { x: clamped, y: clamped } },
        tweenDuration,
        () => {
          this.isTweening = true
          if (this.orbitControls) this.orbitControls.enabled = false
          this.dispatchEvent({ type: ControlEvent.ViewChangedEvent })
          if (shouldPublish) this._publishZoom()
        },
        () => {
          this.isTweening = false
          if (this.orbitControls) this.orbitControls.enabled = true
          // set unclamped FOV as viewer's _target_ FOV
          // actual rendered FOV may be affected by window aspect ratio
          this.viewer?.setFov(fov)
          resolve()
        }
      )
    })
  }

  protected _publishZoom = () => {
    if (!this.viewer) return
    this.dispatchEvent({
      type: ControlEvent.Zoom,
      payload: { zoom: this.viewer.camera.fov },
    })
  }

  protected _isEquivalentPosition(position: Vec2Data): boolean | null {
    if (!this.viewer) return null
    const currentPosition = uvOpposite(worldToUv(this.viewer.camera.position.clone().normalize()))
    // Temporary: Allow for discrepancy in y-position formula
    // Remove when this issue is "done":
    // https://vrify.atlassian.net/browse/VRFY-8445
    const fudgedPosition = { x: currentPosition.x, y: currentPosition.y + 0.008 }
    return uvDistance(position, currentPosition) < 0.0001 || uvDistance(position, fudgedPosition) < 0.0002
  }
}
